import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { RetryButton, StatePanel } from "../components/StatePanel";
import { StatusChip } from "../components/StatusChip";
import { api, formatDate, mutationKey } from "../lib/api";

interface ModerationReport {
  readonly id: string;
  readonly target_type: "post" | "comment";
  readonly target_id: string;
  readonly category: string;
  readonly detail: string | null;
  readonly state: string;
  readonly created_at: string;
  readonly reporter_name: string;
  readonly post_id: string;
  readonly post_type: string;
  readonly condition: string;
  readonly post_state: string;
  readonly comment_body: string | null;
  readonly target_author_name: string;
  readonly site_name: string;
}

interface Decision {
  readonly reportId: string;
  readonly action: string;
  readonly reason: string;
}

export function ModerationPage() {
  const [state, setState] = useState("open");
  const [category, setCategory] = useState("all");
  const queryClient = useQueryClient();
  const reports = useQuery({
    queryKey: ["moderation-reports", state, category],
    queryFn: () =>
      api<{ readonly items: readonly ModerationReport[] }>(
        `/admin/reports?${new URLSearchParams({
          state,
          ...(category === "all" ? {} : { category }),
        }).toString()}`,
      ),
  });
  const decision = useMutation({
    mutationFn: (input: Decision) =>
      api<{ readonly id: string; readonly state: string }>(
        `/admin/reports/${input.reportId}/decision`,
        {
          method: "POST",
          idempotencyKey: mutationKey(`moderation-${input.reportId}`),
          body: JSON.stringify({
            action: input.action,
            reason: input.reason,
          }),
        },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["moderation-reports"] }),
  });
  return (
    <>
      <PageHeader
        eyebrow="Community safety"
        title="Reported content"
        description="Review privacy and safety reports within your assigned program scope. Risk signals never make the decision."
      />
      <div className="filter-bar">
        <label>
          Report state
          <select
            value={state}
            onChange={(event) => setState(event.target.value)}
          >
            <option value="open">Open</option>
            <option value="triaged">Escalated</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
            <option value="all">All</option>
          </select>
        </label>
        <label>
          Report category
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="all">All categories</option>
            <option value="privacy">Privacy</option>
            <option value="harmful_content">Harmful content</option>
            <option value="false_information">False information</option>
            <option value="duplicate_evidence">Duplicate evidence</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      {reports.isLoading ? (
        <StatePanel
          kind="loading"
          title="Loading community reports"
          detail="Applying role and geographic scope."
        />
      ) : reports.isError || reports.data === undefined ? (
        <StatePanel
          kind="error"
          title="Reports are unavailable"
          detail="No moderation decision was changed."
          action={<RetryButton onClick={() => void reports.refetch()} />}
        />
      ) : reports.data.items.length === 0 ? (
        <StatePanel
          kind="empty"
          title="No reports in this view"
          detail="Choose another state or return when a community report arrives."
        />
      ) : (
        <section className="moderation-grid" aria-label="Community reports">
          {reports.data.items.map((report) => (
            <ModerationCard
              key={report.id}
              report={report}
              busy={
                decision.isPending && decision.variables?.reportId === report.id
              }
              error={
                decision.isError && decision.variables?.reportId === report.id
              }
              onDecision={(input) => decision.mutate(input)}
            />
          ))}
        </section>
      )}
    </>
  );
}

function ModerationCard({
  report,
  busy,
  error,
  onDecision,
}: {
  readonly report: ModerationReport;
  readonly busy: boolean;
  readonly error: boolean;
  readonly onDecision: (decision: Decision) => void;
}) {
  const [action, setAction] = useState("escalate");
  const [reason, setReason] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onDecision({ reportId: report.id, action, reason: reason.trim() });
  };
  const final = report.state === "resolved" || report.state === "dismissed";
  return (
    <article className="panel moderation-card">
      <div className="section-heading">
        <div>
          <p className="eyebrow">
            {report.target_type} · {report.category.replaceAll("_", " ")}
          </p>
          <h2>{report.site_name}</h2>
        </div>
        <StatusChip state={report.state} />
      </div>
      <dl className="moderation-facts">
        <div>
          <dt>Reported</dt>
          <dd>{formatDate(report.created_at)}</dd>
        </div>
        <div>
          <dt>Target author</dt>
          <dd>{report.target_author_name}</dd>
        </div>
        <div>
          <dt>Post</dt>
          <dd>
            {report.post_type.replaceAll("_", " ")} · {report.condition}
          </dd>
        </div>
      </dl>
      {report.comment_body === null ? null : (
        <blockquote>{report.comment_body}</blockquote>
      )}
      <p>{report.detail ?? "No additional detail was provided."}</p>
      {final ? null : (
        <form className="moderation-decision" onSubmit={submit}>
          <label>
            Decision
            <select
              value={action}
              onChange={(event) => setAction(event.target.value)}
            >
              <option value="escalate">Escalate for further review</option>
              <option value="dismiss">Dismiss report</option>
              {report.target_type === "post" ? (
                <option value="hide">Hide post from community feed</option>
              ) : (
                <option value="remove_comment">Remove comment</option>
              )}
            </select>
          </label>
          <label>
            Reason
            <textarea
              required
              minLength={3}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {error ? (
            <p className="field-error" role="alert">
              The decision was not saved. Refresh and review the current state.
            </p>
          ) : null}
          <button type="submit" disabled={busy || reason.trim().length < 3}>
            {busy ? "Saving…" : "Confirm decision"}
          </button>
        </form>
      )}
    </article>
  );
}
